import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { profileRouteList, routeList } from "../App";
import { lang } from "../assets/languages/en.language";
import DogNameCurved from "../components/DogNameCurved.component";
import Popup from "../components/Popup.component";
import { CheckMarkIcon } from "../components/svgs/icons/CheckMark.icon";
import { LockIcon } from "../components/svgs/icons/Lock.icon";
import { WarningIcon } from "../components/svgs/icons/Warnings.icon";
import { usePwaViewportWidth } from "../hooks/useScreenWidth";
import api from "../services/api";
import { getVpFontSize, resetLocalEnv } from "../services/utils";
import { useUserStore } from "../store/useUserStore";
import { RoundCta } from "../styles/Button.style";
import { MainContainer, RootContainer } from "../styles/Container.style";
import { BigTitle } from "../styles/Text";
import { Column, Gap } from "../styles/Utils";

export default function ProfilePage() {
  const theme = useTheme();
  const viewportWidth = usePwaViewportWidth();
  const navigate = useNavigate();
  const {
    profile: { dogName, accountVerified, accountVerificationSubmitted },
  } = useUserStore();

  const [showVerifiedPopup, setShowVerifiedPopup] = useState<boolean>(false);
  const [showPendingVrfPopup, setShowPendingVrfPopup] = useState<boolean>(false);
  const [showAccountDeletionPopup, setShowAccountDeletionPopup] = useState<boolean>(false);

  const pageLinks = [
    {
      title: lang.profilePage.PageLinkNames.highlights,
      route: profileRouteList.highlight,
    },
    {
      title: lang.profilePage.PageLinkNames.pictures,
      route: profileRouteList.picture,
    },
    {
      title: lang.profilePage.PageLinkNames.aboutThem,
      route: profileRouteList.aboutThem,
    },
    {
      title: lang.profilePage.PageLinkNames.aboutYouConnectWith,
      route: profileRouteList.aboutYouConnectWith,
    },
  ];

  const openProfilePage = (route: string) => {
    console.log("openProfilePage");
    navigate(route);
  };

  const handleGetVerified = () => {
    console.log(accountVerified, accountVerificationSubmitted);
    if (accountVerified) setShowVerifiedPopup(true);
    else if (!accountVerified && accountVerificationSubmitted) setShowPendingVrfPopup(true);
    else navigate(routeList.verifyAccount);
  };

  const handleAccountDeletion = async () => {
    try {
      await api.accountDeletion();
      logout();
    } catch (err) {
      console.log("Error deleting specific account", err);
      alert("Error deleting specific account");
    }
  };

  const logout = () => {
    resetLocalEnv();
    navigate(routeList.landing);
  };

  return (
    <RootContainer>
      <BigTitle $fontSize={getVpFontSize(viewportWidth, 15)}>{lang.profilePage.title}</BigTitle>
      <MainContainer $justifyContent="space-between" $flexGrow={1} $padding="1rem">
        <Column $gap={1} $alignItem="center">
          <DogNameCurvedContainer $width="80%">
            <DogNameCurved dogName={dogName!} color={theme.color.tertiary} />
          </DogNameCurvedContainer>
          {pageLinks.map((e) => (
            <RoundCta
              key={e.title}
              onClick={() => openProfilePage(e.route)}
              $textAlign="left"
              $background={theme.color.primaryDark}
              $color={theme.color.tertiaryLight}
            >
              {e.title}
            </RoundCta>
          ))}
          <RoundCta
            onClick={handleGetVerified}
            $textAlign="left"
            $background={theme.color.primaryDark}
            $color={theme.color.tertiaryLight}
          >
            {lang.profilePage.PageLinkNames.getVerified}
          </RoundCta>
          <RoundCta
            onClick={() => {
              setShowAccountDeletionPopup(true);
            }}
            $textAlign="left"
            $background={theme.color.primaryDark}
            $color={theme.color.tertiaryLight}
          >
            {lang.profilePage.PageLinkNames.deletion}
          </RoundCta>
        </Column>
        <Gap $height={4} />
        <Image src="/illustrations/girl-front-face-dog.png" />
      </MainContainer>
      {showVerifiedPopup && (
        <Popup
          type="info"
          title={lang.profilePage.statutBadgeInfo.title}
          message={lang.profilePage.statutBadgeInfo.message}
          buttonText={lang.profilePage.statutBadgeInfo.buttonText}
          Icon={CheckMarkIcon}
          action={() => {
            setShowVerifiedPopup(false);
          }}
          onClose={() => {
            setShowVerifiedPopup(false);
          }}
        />
      )}
      {showPendingVrfPopup && (
        <Popup
          type="info"
          title={lang.swiperPage.pendingAccountVerificationPopup.title}
          message={lang.swiperPage.pendingAccountVerificationPopup.message}
          buttonText={lang.swiperPage.pendingAccountVerificationPopup.buttonText}
          Icon={LockIcon}
          action={() => {
            setShowPendingVrfPopup(false);
          }}
          onClose={() => {
            setShowPendingVrfPopup(false);
          }}
        />
      )}
      {showAccountDeletionPopup && (
        <Popup
          type="info"
          title={lang.profilePage.accountDeletionPopup.title}
          message={lang.profilePage.accountDeletionPopup.message}
          buttonText={lang.profilePage.accountDeletionPopup.buttonText}
          Icon={WarningIcon}
          action={() => {
            handleAccountDeletion();
            setShowAccountDeletionPopup(false);
          }}
          onClose={() => {
            setShowAccountDeletionPopup(false);
          }}
        />
      )}
    </RootContainer>
  );
}

const Image = styled.img`
  width: 80%;
  transform: translateY(3rem);
`;

const DogNameCurvedContainer = styled.div<{ $width: string }>`
  width: ${(props) => props.$width};
  margin-bottom: 5%; // Important to overlap button with curves dog name. Later Make the dogname text fit in the viewport.
`;
